import { useMemo, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import { IconButton, MenuItem, Select, FormHelperText } from "@mui/material";
import { chartColorList } from "../constants";
import {
    Tooltip,
    ResponsiveContainer,
    Legend,
    BarChart,
    XAxis,
    YAxis,
    Bar,
} from "recharts";
import { useAppDispatch } from "../../../hooks/StoreHooks";
import { fetchActionModulesDoneThunk } from "../../../store/thunk/reportingThunk";
import { useActionModulesDoneData } from "../../../store/selectors/reportingSelectors";
import { useTranslation } from "react-i18next";
import {
    ActionRow,
    ChartContainer,
    NoDataText,
    OptionContainer,
    OptionRow,
    StyledFormControl
} from "./Widget.styles";
import { NoDataContainer } from "../Dashboard.styles";
import { GeneralButton } from "../../../common/ui/GeneralButton";

const takeOptions: number[] = [5, 10, 15, 20];

const ActionModulesDoneChart = () => {
    const { t } = useTranslation();
    const actionModulesDoneData = useActionModulesDoneData();
    const [take, setTake] = useState<number>(5);
    const [loading, setLoading] = useState<boolean>(false);
    const [emptyChartData, setEmptyChartData] = useState<boolean>(true);
    const dispatch = useAppDispatch();

    const handleSelection = (setter) => (e) => setter(e.target.value);

    const fetchChartData = () => {
        setLoading(false);
        dispatch(fetchActionModulesDoneThunk({ take })).then(() => {
            setLoading(true);
            setEmptyChartData(false);
        });
    };

    const resetChartData = () => {
        setEmptyChartData(true);
    };

    const formattedChartData = useMemo(() => (
        emptyChartData ?
            [{ name: "", modulesDone: 0, fill: "#cccccc" }] :
            actionModulesDoneData.map((elem, i) => ({
                name: `${elem.firstName} ${elem.lastName}`,
                modulesDone: elem.totalDoneActionModules,
                fill: chartColorList[i % chartColorList.length]
            }))
    ), [actionModulesDoneData, emptyChartData]);

    return (
        <>
            <OptionContainer>
                <OptionRow>
                    <StyledFormControl>
                        <Select
                            value={take}
                            onChange={handleSelection(setTake)}
                            IconComponent={ExpandMoreIcon}
                        >
                            {takeOptions.map((item) => (
                                <MenuItem key={item} value={item}>
                                    {item}
                                </MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>{t("dashboardPage.amountOfUsers")}</FormHelperText>
                    </StyledFormControl>
                    <IconButton
                        onClick={resetChartData}
                        aria-label={t("buttons.reset")}
                        sx={{ marginRight: 1, color: "#555" }}
                        size="small"
                    >
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                </OptionRow>
                <ActionRow>
                    <GeneralButton appVariant="widget" onClick={fetchChartData}>
                        {t("buttons.apply")}
                    </GeneralButton>
                </ActionRow>
            </OptionContainer>

            {loading && formattedChartData.length === 0 ? (
                <NoDataContainer>
                    <NoDataText>{t("dashboardPage.noDataAvailable")}</NoDataText>
                </NoDataContainer>
            ) : (
                <ChartContainer>
                    <ResponsiveContainer>
                        <BarChart
                            data={formattedChartData}
                            margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
                            barCategoryGap="20%"
                        >
                            <XAxis dataKey="name" interval={0} angle={-45} textAnchor="end" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="modulesDone" fill={formattedChartData[0]?.fill || "#8884d8"} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartContainer>
            )}
        </>
    );
};

export default ActionModulesDoneChart;
